import {showNotification} from 'platform/components';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useFinancingPromoPreviewMutation} from '@dms/api/core';
import {useCompositionPreviewMutation} from '@dms/api/sales';
import {
  CompositionPreviewApiResponse,
  FinancialProductType,
  FinancingCompositionScene,
} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {toCompositionScene} from '../utils/toCompositionScene';

export type CompositionServerPreviewTarget =
  | {mode: 'watermark'}
  | {
      mode: 'financing';
      financialProductType: FinancialProductType;
      sourcePhotoIndex: number | null;
    };

/**
 * Requests the authoritative server render of a scene. The last render is kept with the scene it
 * was made for and is valid only while the editor holds that exact scene: any edit makes it stale,
 * so neither the image nor its issues are ever shown over a newer scene.
 *
 * Watermark scenes go to the composition preview; financing scenes (which may hold the vehicle
 * photo slot and `{{KEY}}` tokens) go to the financing-aware promo preview, which fills the slot
 * and tokens with sample data and reports business problems as warnings.
 */
export function useCompositionServerPreview(target: CompositionServerPreviewTarget) {
  const [compositionPreview, {isLoading: isCompositionPreviewLoading}] =
    useCompositionPreviewMutation();
  const [financingPromoPreview, {isLoading: isFinancingPreviewLoading}] =
    useFinancingPromoPreviewMutation();
  const [lastPreview, setLastPreview] = useState<{
    scene: FinancingCompositionScene;
    response: CompositionPreviewApiResponse;
  } | null>(null);
  // The render image replaces the editable layers only after an explicit Preview click.
  const [shownScene, setShownScene] = useState<FinancingCompositionScene | null>(null);

  const render = (scene: FinancingCompositionScene) => {
    if (target.mode === 'financing') {
      return financingPromoPreview({
        financingPromoPreviewRequestBody: {
          scene,
          financialProductType: target.financialProductType,
          sourcePhotoIndex: target.sourcePhotoIndex,
        },
      }).unwrap();
    }

    const watermarkScene = toCompositionScene(scene);
    if (watermarkScene === null) {
      return Promise.reject(new Error('A watermark scene must not contain a photo layer'));
    }

    return compositionPreview({compositionPreviewRequestBody: {scene: watermarkScene}}).unwrap();
  };

  const fetchPreview = (scene: FinancingCompositionScene) =>
    render(scene)
      .then((response) => {
        setLastPreview({scene, response});
        return response;
      })
      .catch((error: ApiError) => {
        handleApiError(error);
        return null;
      });

  const getPreviewFor = (scene: FinancingCompositionScene) =>
    lastPreview?.scene === scene ? lastPreview.response : null;

  // The kept render while it is current, otherwise a fresh one.
  const getCurrentPreview = (scene: FinancingCompositionScene) => {
    const preview = getPreviewFor(scene);
    return isNotNil(preview) ? Promise.resolve(preview) : fetchPreview(scene);
  };

  const showPreview = (scene: FinancingCompositionScene) =>
    getCurrentPreview(scene).then((response) => {
      if (isNil(response)) {
        return;
      }
      setShownScene(scene);
      if (response.warnings.length > 0) {
        showNotification.warning(
          i18n.t('entity.watermark.composition.notifications.previewHasWarnings')
        );
      }
    });

  return {
    isLoading: isCompositionPreviewLoading || isFinancingPreviewLoading,
    showPreview,
    hidePreview: () => setShownScene(null),
    isShowingPreviewOf: (scene: FinancingCompositionScene) =>
      scene === shownScene && isNotNil(getPreviewFor(scene)),
    getPreviewFor,
    getCurrentPreview,
  };
}
